import { ref } from "vue";
import { EventManager, Events } from "@events";
import { useVideoCallState } from "@/stores/videoCallState";

/**
 * Module-level cache: rtcId → epoch millis.
 * Shared across ALL component instances so the timestamp captured by the first
 * component to observe RecordingStarted is available to every later consumer.
 */
const recordingStartTimeCache = new Map();

/**
 * Tracks which rtcIds have already been adjusted by the _ts offset
 * so we only apply the correction once.
 */
const tsOffsetApplied = new Set();

/**
 * Shared composable that tracks when the recording started for a given rtcId.
 * Uses a module-level cache so all components referencing the same rtcId share
 * the same captured timestamp (whoever captures it first wins).
 *
 * @param {string} rtcId
 * @returns {{ recordingStartTime: import('vue').Ref<number|null>, getElapsedSeconds: () => number, setupListener: () => Promise<() => void> }}
 */
export function useRecordingStartTime(rtcId) {
  // Seed from cache if another component already captured the value
  const recordingStartTime = ref(recordingStartTimeCache.get(rtcId) ?? null);
  const state = useVideoCallState(rtcId);

  function setRecordingStartTime(value) {
    recordingStartTime.value = value;
    if (!recordingStartTimeCache.has(rtcId)) {
      recordingStartTimeCache.set(rtcId, value);
    }
  }

  /**
   * Registers the RecordingStarted listener, awaits webrtcConnectedPromise as
   * fallback, and returns a cleanup function.
   * Returns a Promise that resolves (with the cleanup fn) once recordingStartTime
   * is guaranteed to be set.
   */
  async function setupListener() {
    const eventEmitter = EventManager.getEventEmitter(rtcId);

    const handler = () => {
      setRecordingStartTime(Date.now());
    };

    eventEmitter.on(Events.RecordingStarted, handler);

    // When the WDE returns the _ts offset (ms between video timeline start and
    // first WebM blob), shift recordingStartTime backwards so snippet timing
    // aligns with the merged video's timeline.
    const tsHandler = ({ tsOffsetMs }) => {
      if (!tsOffsetMs || tsOffsetMs <= 0) return;
      if (tsOffsetApplied.has(rtcId)) return;
      tsOffsetApplied.add(rtcId);

      const adjustedTime = Date.now() - tsOffsetMs;
      // Only shift earlier (never push the start time forward)
      if (recordingStartTime.value == null || adjustedTime < recordingStartTime.value) {
        recordingStartTime.value = adjustedTime;
        recordingStartTimeCache.set(rtcId, adjustedTime);
        console.log(`[useRecordingStartTime] Adjusted recordingStartTime by -${tsOffsetMs}ms for ${rtcId}`);
      }
    };

    eventEmitter.on(Events.RecordingTimestampOffset, tsHandler);

    // If another instance already captured the time, use it immediately
    if (recordingStartTimeCache.has(rtcId) && !recordingStartTime.value) {
      recordingStartTime.value = recordingStartTimeCache.get(rtcId);
    }

    // Fallback: if RecordingStarted already fired before we mounted
    if (!recordingStartTime.value) {
      await state.webrtcConnectedPromise;
      if (!recordingStartTime.value) {
        setRecordingStartTime(Date.now());
      }
    }

    // Return cleanup function
    return () => {
      eventEmitter.off(Events.RecordingStarted, handler);
      eventEmitter.off(Events.RecordingTimestampOffset, tsHandler);
    };
  }

  function getElapsedSeconds() {
    return recordingStartTime.value
      ? Math.floor((Date.now() - recordingStartTime.value) / 1000)
      : 0;
  }

  return {
    recordingStartTime,
    getElapsedSeconds,
    setupListener,
  };
}
